import React, { useState, useEffect } from 'react';
import { FileText, Plus, Pencil, Trash2, X, Save, ChevronDown, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'allgemein', label: 'Allgemein', color: 'bg-gray-100 text-gray-700' },
  { key: 'willkommen', label: 'Willkommen', color: 'bg-green-100 text-green-700' },
  { key: 'info', label: 'Info', color: 'bg-blue-100 text-blue-700' },
  { key: 'seelsorge', label: 'Seelsorge', color: 'bg-purple-100 text-purple-700' },
];

const getCategoryStyle = (cat) => {
  const found = CATEGORIES.find(c => c.key === cat);
  return found ? found.color : 'bg-gray-100 text-gray-700';
};

const AdminTemplatesManager = ({ onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', body: '', category: 'allgemein' });

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/templates`, { headers: authHeaders() });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleSaveNew = async () => {
    if (!form.name.trim() || !form.subject.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/templates`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowNew(false);
        setForm({ name: '', subject: '', body: '', category: 'allgemein' });
        fetchTemplates();
      }
    } catch (err) {
      console.error('Error creating template:', err);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/templates/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setEditingId(null);
        setForm({ name: '', subject: '', body: '', category: 'allgemein' });
        fetchTemplates();
      }
    } catch (err) {
      console.error('Error updating template:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Vorlage "${name}" wirklich loeschen?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/templates/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, subject: t.subject, body: t.body, category: t.category || 'allgemein' });
    setShowNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowNew(false);
    setForm({ name: '', subject: '', body: '', category: 'allgemein' });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="templates-manager">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-bold text-gray-900 flex items-center gap-2" data-testid="templates-heading">
          <FileText className="w-5 h-5 text-[#0f2440]" />
          Antwort-Vorlagen ({templates.length})
        </h2>
        <button
          onClick={() => { setShowNew(true); setEditingId(null); setForm({ name: '', subject: '', body: '', category: 'allgemein' }); }}
          data-testid="template-add-btn"
          className="px-4 py-2 bg-[#0f2440] text-white rounded-lg text-sm font-medium hover:bg-[#1a3a5c] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Neue Vorlage
        </button>
      </div>

      {/* New / Edit Form */}
      {(showNew || editingId) && (
        <div className="p-5 border-b border-gray-200 bg-blue-50/30" data-testid="template-form">
          <h3 className="text-sm font-bold text-gray-700 mb-3">
            {editingId ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="template-form-name"
                placeholder="z.B. Willkommen"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Kategorie</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                data-testid="template-form-category"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CATEGORIES.map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Betreff</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              data-testid="template-form-subject"
              placeholder="E-Mail Betreff"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Nachricht <span className="text-gray-400">(Platzhalter: {'{name}'}, {'{email}'})</span>
            </label>
            <textarea
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              rows={6}
              data-testid="template-form-body"
              placeholder="Vorlage-Text..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => editingId ? handleUpdate(editingId) : handleSaveNew()}
              data-testid="template-form-save"
              className="px-5 py-2 bg-[#0f2440] text-white rounded-lg text-sm font-medium hover:bg-[#1a3a5c] transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingId ? 'Aktualisieren' : 'Speichern'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Templates List */}
      {loading ? (
        <div className="p-8 text-center text-gray-400">Lade Vorlagen...</div>
      ) : templates.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>Noch keine Vorlagen vorhanden</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {templates.map((t) => (
            <div
              key={t.id}
              className="p-4 hover:bg-gray-50/50 transition-colors group"
              data-testid={`template-item-${t.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{t.name}</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getCategoryStyle(t.category)}`}>
                      {CATEGORIES.find(c => c.key === t.category)?.label || t.category}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">Betreff: {t.subject}</p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{t.body?.substring(0, 120)}...</p>
                </div>
                <div className="flex items-center gap-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onSelectTemplate && (
                    <button
                      onClick={() => onSelectTemplate(t)}
                      data-testid={`template-use-${t.id}`}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                      title="Vorlage verwenden"
                    >
                      Verwenden
                    </button>
                  )}
                  <button
                    onClick={() => startEdit(t)}
                    data-testid={`template-edit-${t.id}`}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Bearbeiten"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id, t.name)}
                    data-testid={`template-delete-${t.id}`}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Loeschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Compact template picker for compose forms
export const TemplatePicker = ({ onSelect }) => {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const loadTemplates = async () => {
    if (templates.length > 0) {
      setOpen(!open);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/templates`, { headers: authHeaders() });
      const data = await res.json();
      setTemplates(data.templates || []);
      setOpen(true);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (t) => {
    onSelect(t);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={loadTemplates}
        data-testid="template-picker-btn"
        className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors flex items-center gap-2"
      >
        <FileText className="w-4 h-4" />
        {loading ? 'Lade...' : 'Vorlage'}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && templates.length > 0 && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-80 max-h-72 overflow-y-auto" data-testid="template-picker-dropdown">
          <div className="p-2">
            {templates.map(t => (
              <button
                key={t.id}
                onClick={() => selectTemplate(t)}
                data-testid={`template-pick-${t.id}`}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getCategoryStyle(t.category)}`}>
                    {CATEGORIES.find(c => c.key === t.category)?.label || t.category}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{t.subject}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {open && templates.length === 0 && !loading && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-64 p-4 text-center text-gray-400 text-sm">
          Keine Vorlagen vorhanden
        </div>
      )}
    </div>
  );
};

export default AdminTemplatesManager;
